// AI assistants (Claude, ChatGPT, Gemini, GitHub Copilot) connecting to Froth TestOps over MCP.
// Dark band shared by /integration and /froth-ai; content mirrors the product docs (/docs on each tenant).
import { useState } from "react";
import { Check, Copy, ArrowUpRight, Plug, KeyRound, Eye, Users, ShieldCheck, BadgeCheck } from "lucide-react";
import { SiClaude, SiOpenai, SiGooglegemini, SiGithubcopilot } from "react-icons/si";
import "../../css/McpConnect.css";

const MCP_BASE = "https://mcp.frothtestops.com/mcp/";
const DOCS_BASE = "https://rd.frothtestops.com/docs";

export const assistants = [
  { name: "Claude", client: "Claude Desktop / Code", Icon: SiClaude, color: "#d97757", slug: "claude", verified: true },
  { name: "ChatGPT", client: "MCP connector support", Icon: SiOpenai, color: "#0d1424", slug: "chatgpt" },
  { name: "Gemini", client: "MCP-capable integrations", Icon: SiGooglegemini, color: "#7b6cf0", slug: "gemini" },
  { name: "GitHub Copilot", client: "MCP-capable client", Icon: SiGithubcopilot, color: "#0d1424", slug: "copilot" },
];

const facts = [
  { icon: Plug, title: "Remote MCP", text: "Nothing to install" },
  { icon: KeyRound, title: "OAuth 2.0", text: "Your existing account" },
  { icon: Eye, title: "Read-only", text: "Can't change records" },
  { icon: Users, title: "Your permissions", text: "Only what you can see" },
];

const safety = [
  "We never ask for your Froth TestOps password.",
  "Never paste access or refresh tokens anywhere.",
  "Sign-in always happens on the Froth TestOps page.",
];

// Type a tenant name to build the MCP endpoint, then copy it.
function McpEndpoint({ id }) {
  const [tenant, setTenant] = useState("");
  const [copied, setCopied] = useState(false);
  const url = MCP_BASE + (tenant || "your-tenant");

  const copy = () => {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  };

  return (
    <div className="mcp-endpoint">
      <label className="mcp-endpoint__label" htmlFor={`${id}-tenant`}>
        Your MCP endpoint
      </label>
      <div className="mcp-endpoint__row">
        <code className="mcp-endpoint__url">
          {MCP_BASE}
          <span className={tenant ? "" : "is-placeholder"}>{tenant || "your-tenant"}</span>
        </code>
        <button type="button" className="mcp-endpoint__copy" onClick={copy} aria-label="Copy MCP endpoint">
          {copied ? <Check size={16} aria-hidden="true" /> : <Copy size={16} aria-hidden="true" />}
          <span>{copied ? "Copied" : "Copy"}</span>
        </button>
      </div>
      <div className="mcp-endpoint__tenant">
        <input
          id={`${id}-tenant`}
          type="text"
          inputMode="url"
          autoComplete="off"
          spellCheck="false"
          placeholder="Tenant name, e.g. acme"
          value={tenant}
          onChange={e => setTenant(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
        />
        <p>
          It's the first part of your sign-in address: <b>acme</b>.frothtestops.com. You can also copy it from
          Docs.
        </p>
      </div>
    </div>
  );
}

function AssistantSetup({ id }) {
  const [active, setActive] = useState(0);
  const a = assistants[active];

  const steps = [
    { title: "Add a remote MCP server", text: `Open ${a.name}'s connector or integration settings.` },
    { title: "Paste your endpoint", text: "Use your MCP endpoint, with your tenant name, and save." },
    { title: "Sign in with OAuth", text: "Approve the connection on the Froth TestOps sign-in page." },
    { title: "Ask about your QA data", text: `Try "List my accessible Froth TestOps projects."` },
  ];

  return (
    <div className="mcp-assist">
      <div className="mcp-assist__tabs" role="tablist" aria-label="AI assistant">
        {assistants.map(({ name, Icon, color }, i) => (
          <button
            key={name}
            type="button"
            role="tab"
            id={`${id}-tab-${i}`}
            aria-selected={i === active}
            aria-controls={`${id}-panel`}
            className={`mcp-assist__tab${i === active ? " is-active" : ""}`}
            onClick={() => setActive(i)}
          >
            <span className="mcp-assist__mark" style={{ color }}>
              <Icon aria-hidden="true" />
            </span>
            {name}
          </button>
        ))}
      </div>

      <div className="mcp-assist__panel" role="tabpanel" id={`${id}-panel`} aria-labelledby={`${id}-tab-${active}`}>
        <div className="mcp-assist__head">
          <div>
            <h3>Connect {a.name}</h3>
            <p>{a.client}</p>
          </div>
          {a.verified && (
            <span className="mcp-assist__status is-verified">
              <BadgeCheck size={14} aria-hidden="true" /> Verified steps
            </span>
          )}
        </div>

        <ol className="mcp-assist__steps">
          {steps.map((s, i) => (
            <li key={s.title}>
              <span className="mcp-assist__num">{i + 1}</span>
              <div>
                <strong>{s.title}</strong>
                <span>{s.text}</span>
              </div>
            </li>
          ))}
        </ol>

        <a className="mcp-assist__guide" href={`${DOCS_BASE}/${a.slug}`} target="_blank" rel="noopener noreferrer">
          Full {a.name} setup guide <ArrowUpRight size={16} aria-hidden="true" />
        </a>
      </div>
    </div>
  );
}

export default function McpConnect({
  id = "ai-assistants",
  title = (
    <>
      Ask your AI assistant about <span>your QA data</span>
    </>
  ),
}) {
  return (
    <section className="ft-section mcp" id={id}>
      <div className="ft-container">
        <div className="mcp__grid">
          <div>
            <span className="mcp__eyebrow">
              <span className="mcp__new">New</span> MCP connectors
            </span>
            <h2 className="ft-heading">{title}</h2>
            <p className="mcp__lead">
              Connect Claude, ChatGPT, Gemini or GitHub Copilot to Froth TestOps over the Model Context Protocol.
              Secure, read-only access, signed in with your own account.
            </p>
            <ul className="mcp__facts">
              {facts.map(({ icon: Icon, title: t, text }) => (
                <li key={t}>
                  <Icon size={18} aria-hidden="true" />
                  <div>
                    <strong>{t}</strong>
                    <span>{text}</span>
                  </div>
                </li>
              ))}
            </ul>
            <McpEndpoint id={id} />
          </div>
          <AssistantSetup id={id} />
        </div>
        <ul className="mcp__safety">
          <li className="mcp__safety-title">
            <ShieldCheck size={16} aria-hidden="true" /> Security
          </li>
          {safety.map(s => (
            <li key={s}>
              <Check size={14} aria-hidden="true" /> {s}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
